// @contexts/Loading.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { requestStateIdle } from "@ps-bridge/models/brands/requestState";
import type { RequestState } from "@ps-bridge/models/brands/requestState";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import { useIoState } from '@utils/useIoState';
import { mkLoadingController, type LoadingController, type LoadingError } from "@ps-bridge/controllers/loading";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { requireContext } from "@contexts/requireContext";

import type { StateHandle, StateRead } from "@ps-bridge/capabilitiesTypes";

// Contexts
const none = <A,>(): OptionTs<A> => Option.none();

const LoadingStateHandleContext = createContext<OptionTs<StateHandle<RequestState>>>(none());
const LoadingErrorHandleContext = createContext<OptionTs<StateHandle<LoadingError>>>(none());
const LoadingControllerContext = createContext<OptionTs<LoadingController>>(none());

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const stateHandle = useIoState<RequestState>(requestStateIdle);
    const errorHandle = useIoState<LoadingError>({
        message: makeErrorMessageText(""),
        info: none(),
    });
    const { read: stateRead, set: stateSet } = stateHandle;
    const { read: errorRead, set: errorSet } = errorHandle;
    const controller = useMemo(
        () => mkLoadingController({ state: { read: stateRead, set: stateSet }, error: { read: errorRead, set: errorSet } }),
        [stateRead, stateSet, errorRead, errorSet],
    );

    return (
        <LoadingStateHandleContext.Provider value={Option.some(stateHandle)}>
            <LoadingErrorHandleContext.Provider value={Option.some(errorHandle)}>
                <LoadingControllerContext.Provider value={Option.some(controller)}>
                    {children}
                </LoadingControllerContext.Provider>
            </LoadingErrorHandleContext.Provider>
        </LoadingStateHandleContext.Provider>
    );
};

export const useLoadingStateHandle = (): StateHandle<RequestState> => {
    return requireContext(
        useContext(LoadingStateHandleContext),
        'useLoadingStateHandle must be used within a LoadingProvider',
    );
};

export const useLoadingErrorHandle = (): StateHandle<LoadingError> => {
    return requireContext(
        useContext(LoadingErrorHandleContext),
        'useLoadingErrorHandle must be used within a LoadingProvider',
    );
};

export const useLoadingController = (): LoadingController => {
    return requireContext(
        useContext(LoadingControllerContext),
        'useLoadingController must be used within a LoadingProvider',
    );
};

export const useLoadingState = (): StateRead<RequestState> => useLoadingStateHandle().read;

export const useLoadingError = (): StateRead<LoadingError> => useLoadingErrorHandle().read;
